import React, { useMemo, useState } from 'react';
import MasterTimetableView from './MasterTimetableView';
import WeeklyTimetableView from './WeeklyTimetableView';
import DailyTimetableView from './DailyTimetableView';
import AnalyticsDashboard from './AnalyticsDashboard';
import { Button, Popover, Tabs, Card, Typography, Badge } from 'antd';
import { 
  EditOutlined, 
  BarChartOutlined, 
  AppstoreOutlined,
  CalendarOutlined,
  CloseOutlined,
  CheckOutlined,
  LockOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { TabPane } = Tabs;
const { Text } = Typography;

const ViewTimetable = ({
  viewMode,
  setViewMode,
  weekTemplate,
  baseWeekDocId,
  baseMeta = {},
  subjects,
  selectedClassId,
  selectedAcademicYear,
  selectedSemester,
  selectedTerm, 
  isSemesterBased,
  academicYears,
  semesters,
  tenantId,
  onEdit,
  onDelete,
  loadingButton,
  getSubjectName,
  showModal,
  deleteTimetable,
  isLoading,
  labBatches,
  facultyData = [],
  token,
  onOverrideClick,
  electiveGroups = [],
  loadingElectives = false
}) => {
  const [selectedOverrideDate, setSelectedOverrideDate] = useState(moment().format('YYYY-MM-DD'));
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('timetable');
  
  // Faculty name map
  const facultyNameMap = useMemo(() => {
    const map = {};
    (facultyData || []).forEach(f => {
      const id = String(f.userId || f._id || f.staffId || f.id || '').trim();
      const name = [f.firstName, f.middleName, f.lastName].filter(Boolean).join(' ').trim() || f.name || f.fullName || '';
      if (id && name) map[id] = name;
    });

    (subjects || []).forEach(sub => {
      const assigned = sub.assignedFaculty || sub.assigned_faculty || sub.facultyAssigned || [];
      (assigned || []).forEach(a => {
        const id = String(a.userId || a._id || a.staffId || a.id || '').trim();
        const name = [a.firstName, a.middleName, a.lastName].filter(Boolean).join(' ').trim() || a.fullName || '';
        if (id && name && !map[id]) map[id] = name;
      });
    });
    return map;
  }, [facultyData, subjects]);

  const handleOverride = () => {
    if (!selectedClassId || !selectedAcademicYear) {
      showModal('Please select a class and academic year first', 'warning');
      return;
    }
    if (onOverrideClick) {
      onOverrideClick(selectedOverrideDate);
      setPopoverVisible(false);
    }
  };

  const isOverrideAvailable = selectedClassId && selectedAcademicYear;

  const overrideContent = (
    <div className="w-80 p-2">
      <div className="flex items-center gap-3 mb-4 px-2">
        <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
          <CalendarOutlined className="text-white text-lg" />
        </div>
        <div>
          <div className="text-sm font-bold text-gray-800">Daily Override</div>
          <div className="text-xs text-gray-500">Create custom schedule for specific date</div>
        </div>
        {!isOverrideAvailable && (
          <Badge status="warning" text="Locked" className="ml-auto" />
        )}
      </div>

      <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 p-4 rounded-2xl mb-4 border border-gray-200/50">
        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
          Select Target Date
        </div>
        
        <div className="relative">
          <input 
            type="date"
            value={selectedOverrideDate}
            onChange={(e) => setSelectedOverrideDate(e.target.value)}
            disabled={!isOverrideAvailable}
            className={`
              w-full bg-white border-2 rounded-xl px-4 py-3 
              text-sm font-medium text-gray-700 
              outline-none transition-all
              ${isOverrideAvailable 
                ? 'border-gray-200 hover:border-blue-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100' 
                : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
              }
            `}
            style={{ appearance: 'none', WebkitAppearance: 'none' }}
          />
          {!isOverrideAvailable && (
            <div className="absolute inset-0 bg-gray-50/50 rounded-xl flex items-center justify-center backdrop-blur-[1px]">
              <LockOutlined className="text-gray-300 mr-1" />
              <span className="text-xs text-gray-400">Select class & year first</span>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-start gap-2 px-1">
          <div className="mt-1.5 w-1 h-1 rounded-full bg-blue-400" />
          <Text className="text-[10px] text-gray-400 leading-relaxed">
            Override for <span className="font-bold text-gray-600">{moment(selectedOverrideDate).format('dddd, MMMM Do, YYYY')}</span>
          </Text>
        </div>
      </div>

      <div className="flex gap-2">
        <button 
          onClick={() => setPopoverVisible(false)}
          className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
        >
          <CloseOutlined /> CANCEL
        </button>
        <button 
          onClick={handleOverride}
          disabled={!isOverrideAvailable}
          className={`
            flex-1 py-3 rounded-xl text-xs font-bold 
            transition-all flex items-center justify-center gap-2
            ${isOverrideAvailable
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          <CheckOutlined /> MODIFY
        </button>
      </div>

      {!isOverrideAvailable && (
        <div className="mt-3 text-center">
          <Text type="secondary" className="text-[10px]">
            Please select a class and academic year first
          </Text>
        </div>
      )}
    </div>
  );

  const renderTimetableView = () => {
    switch (viewMode) {
      case 'base':
        return (
          <MasterTimetableView
            weekTemplate={weekTemplate}
            subjects={subjects}
            getSubjectName={getSubjectName}
            deleteTimetable={deleteTimetable}
            isSemesterBased={isSemesterBased}
            isLoading={isLoading}
            labBatches={labBatches}
            facultyData={facultyData}
            facultyNameMap={facultyNameMap}
            baseMeta={baseMeta}
            onEditTimetable={onEdit}
            onViewAnalytics={() => setActiveAnalyticsTab('analytics')}
            electiveGroups={electiveGroups}
          />
        );
      case 'weekly':
        return (
          <WeeklyTimetableView
            tenantId={tenantId}
            classId={selectedClassId}
            academicYearId={selectedAcademicYear}
            semesterType={selectedSemester}
            termId={selectedTerm?._id}
            termName={selectedTerm?.termLabel || selectedTerm?.termName}
            isSemesterBased={isSemesterBased}
            facultyData={facultyData}
            subjects={subjects}
            token={token}
            onOverrideClick={onOverrideClick}
            electiveGroups={electiveGroups}
            labBatches={labBatches}  // FIXED: Added labBatches prop
          />
        );
      case 'daily':
        return (
          <DailyTimetableView
            tenantId={tenantId}
            classId={selectedClassId}
            academicYearId={selectedAcademicYear}
            semesterType={selectedSemester}
            termId={selectedTerm?._id}
            termName={selectedTerm?.termLabel || selectedTerm?.termName}
            isSemesterBased={isSemesterBased}
            facultyData={facultyData}
            subjects={subjects}
            token={token}
            onOverrideClick={onOverrideClick}
            electiveGroups={electiveGroups}
            labBatches={labBatches}  // FIXED: Added labBatches prop
          />
        );
      default:
        return null;
    }
  };

  const renderAnalyticsView = () => (
    <AnalyticsDashboard
      weekTemplate={weekTemplate}
      subjects={subjects}
      facultyData={facultyData}
      labBatches={labBatches}
      electiveGroups={electiveGroups}
      baseMeta={baseMeta}
      facultyNameMap={facultyNameMap}
    />
  );

  return (
    <div className="space-y-6">
      <Card className="shadow-sm border-0 rounded-3xl overflow-hidden">
        <Tabs
          activeKey={activeAnalyticsTab}
          onChange={setActiveAnalyticsTab}
          type="card"
          size="large"
          className="custom-main-tabs"
        >
          <TabPane
            tab={
              <span className="flex items-center gap-2 py-1">
                <AppstoreOutlined />
                Timetable View
              </span>
            }
            key="timetable"
          >
            <div className="flex flex-wrap border-b border-gray-100 mb-8">
              {['base', 'weekly', 'daily'].map((mode) => (
                <button
                  key={mode}
                  className={`px-8 py-4 text-xs font-black uppercase tracking-widest transition-all relative ${
                    viewMode === mode 
                      ? 'text-blue-600' 
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                  onClick={() => setViewMode(mode)}
                >
                  {mode === 'base' ? 'Master' : mode}
                  {viewMode === mode && (
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 rounded-t-full" />
                  )}
                </button>
              ))}
            </div>

            {activeAnalyticsTab === 'timetable' && viewMode === 'base' && (
              <div className="flex flex-wrap gap-3 mb-8 px-2">
                <Popover
                  content={overrideContent}
                  trigger="click"
                  open={popoverVisible}
                  onOpenChange={setPopoverVisible}
                  placement="bottomLeft"
                  overlayClassName="modern-popover"
                  overlayInnerStyle={{ 
                    borderRadius: '20px', 
                    padding: '16px',
                    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)'
                  }}
                >
                  <Button 
                    type="primary" 
                    icon={<EditOutlined />}
                    className="h-11 px-6 rounded-2xl font-black text-xs shadow-xl shadow-blue-100 flex items-center gap-2"
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none'
                    }}
                    disabled={!selectedClassId || !selectedAcademicYear}
                  >
                    DAILY OVERRIDE
                  </Button>
                </Popover>

                <Button 
                  onClick={onEdit}
                  icon={<EditOutlined />}
                  className="h-11 px-6 rounded-2xl font-black text-xs border-gray-200 flex items-center gap-2 hover:border-blue-300 hover:text-blue-600"
                >
                  EDIT MASTER
                </Button>
                
                <Button 
                  type="dashed"
                  icon={<BarChartOutlined />}
                  onClick={() => setActiveAnalyticsTab('analytics')}
                  className="h-11 px-6 rounded-2xl font-black text-xs flex items-center gap-2 border-gray-200 hover:border-purple-300 hover:text-purple-600"
                >
                  ANALYTICS
                </Button>
              </div>
            )}

            <div className="min-h-[500px]">
              {renderTimetableView()}
            </div>
          </TabPane>

          <TabPane
            tab={
              <span className="flex items-center gap-2 py-1">
                <BarChartOutlined />
                Analytics Dashboard
              </span>
            }
            key="analytics"
          >
            <div className="mb-6 p-4">
              <Button 
                type="link" 
                icon={<AppstoreOutlined />}
                onClick={() => setActiveAnalyticsTab('timetable')}
                className="font-black text-blue-600 uppercase text-xs tracking-widest"
              >
                ← Back to Timetable
              </Button>
            </div>
            {renderAnalyticsView()}
          </TabPane>
        </Tabs>
      </Card>

      <style jsx global>{`
        .custom-main-tabs .ant-tabs-nav {
          background: #f8fafc;
          padding: 8px 16px 0;
          margin-bottom: 0 !important;
        }
        .custom-main-tabs .ant-tabs-tab {
          border: none !important;
          background: transparent !important;
          transition: all 0.3s;
        }
        .custom-main-tabs .ant-tabs-tab-active {
          background: white !important;
        }
        .modern-popover .ant-popover-inner {
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        input[type="date"]::-webkit-calendar-picker-indicator {
          background-color: transparent;
          cursor: pointer;
          opacity: 0.6;
          filter: invert(48%) sepia(79%) saturate(2476%) hue-rotate(202deg) brightness(101%) contrast(101%);
        }
        input[type="date"]:disabled::-webkit-calendar-picker-indicator {
          opacity: 0.3;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ViewTimetable;